"use client";

import { motion } from "framer-motion";
import { Sparkles, Heart, Scissors, Star, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const WHATSAPP_NUMBER = "919876543210";

interface IntentOption {
  keyword: string;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const intentOptions: IntentOption[] = [
  {
    keyword: "ZAKLYN",
    label: "Custom Outfit",
    description: "New stitching or general enquiry",
    icon: Sparkles,
    color: "text-amber-600",
    bgColor: "bg-amber-50 hover:bg-amber-100 border-amber-200",
  },
  {
    keyword: "BRIDE",
    label: "Bridal & Wedding",
    description: "Heavy work, lehengas, wedding wear",
    icon: Heart,
    color: "text-rose-600",
    bgColor: "bg-rose-50 hover:bg-rose-100 border-rose-200",
  },
  {
    keyword: "FIT",
    label: "Alterations",
    description: "Fit issues, resizing, refinement",
    icon: Scissors,
    color: "text-teal-600",
    bgColor: "bg-teal-50 hover:bg-teal-100 border-teal-200",
  },
  {
    keyword: "BLOSSOM",
    label: "Special Collab",
    description: "Campaign or collaboration",
    icon: Star,
    color: "text-sky-600",
    bgColor: "bg-sky-50 hover:bg-sky-100 border-sky-200",
  },
];

function getWhatsAppLink(keyword: string) {
  const message = encodeURIComponent(keyword);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
}

export function IntentSelector() {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-background to-secondary/20">
      <div className="zaklyn-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <MessageCircle className="h-4 w-4" />
            Start on WhatsApp
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
            What can we help you with?
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Tap your need below to start a conversation. Our team will guide you through everything on WhatsApp.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto">
          {intentOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <motion.a
                key={option.keyword}
                href={getWhatsAppLink(option.keyword)}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cn(
                  "group relative flex flex-col items-center p-6 rounded-2xl border-2 transition-all duration-300",
                  "hover:shadow-lg hover:-translate-y-1",
                  option.bgColor
                )}
              >
                <div
                  className={cn(
                    "w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110",
                    "bg-white shadow-sm"
                  )}
                >
                  <Icon className={cn("h-7 w-7", option.color)} />
                </div>
                <span className="font-display text-lg font-semibold text-foreground mb-1">
                  {option.label}
                </span>
                <span className="text-sm text-muted-foreground text-center">
                  {option.description}
                </span>
                <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-muted-foreground/70">
                  <span className="bg-white/80 px-2 py-1 rounded-md border border-current/10">
                    DM &quot;{option.keyword}&quot;
                  </span>
                </div>
              </motion.a>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Prefer Instagram? DM us the keyword on{" "}
          <a
            href="https://instagram.com/zaklyn"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-medium"
          >
            @zaklyn
          </a>
        </motion.p>
      </div>
    </section>
  );
}
